import React from 'react';
import { Box, Card, CardActionArea, CardContent, Skeleton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { LoadError } from '../analytics/owner/parts';

/** First load: a skeleton of the tab's shape, or the error in plain words with Try again (as on Analytics). */
export const FirstLoad: React.FC<{ error: string | null; what: string; onRetry: () => void; tiles?: number }> = ({ error, what, onRetry, tiles = 4 }) =>
  error ? (
    <LoadError what={what} message={error} onRetry={onRetry} />
  ) : (
    <Box aria-busy="true" aria-label={`Loading ${what}`}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${tiles}, 1fr)` }, gap: 2, mb: 3 }}>
        {Array.from({ length: tiles }, (_, i) => (
          <Skeleton key={i} variant="rounded" height={132} />
        ))}
      </Box>
      <Skeleton variant="rounded" height={300} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={220} />
    </Box>
  );

/** A later load failed: say so, keep showing the last figures. */
export const StaleNotice: React.FC<{ error: string | null; onRetry: () => void }> = ({ error, onRetry }) =>
  error ? (
    <Box sx={{ mb: 2 }}>
      <LoadError what="the latest figures" message={error} onRetry={onRetry} severity="warning">
        Still showing the figures loaded before.
      </LoadError>
    </Box>
  ) : null;

/** Period choice as a segmented control. */
export function RangeToggle<T extends number>({ value, options, onChange, label }: { value: T; options: ReadonlyArray<{ days: T; label: string }>; onChange: (v: T) => void; label: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" color="text.secondary" id={`${label}-range`}>
        Last
      </Typography>
      <ToggleButtonGroup size="small" exclusive value={value} onChange={(_, v: T | null) => v != null && onChange(v)} aria-labelledby={`${label}-range`}>
        {options.map((o) => (
          <ToggleButton key={o.days} value={o.days} sx={{ px: 1.5, textTransform: 'none' }}>
            {o.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}

/**
 * A headline tile that moves to a list further down the page rather than
 * opening records (the shared StatTile only opens records). Laid out like
 * StatTile so the two sit together in one row.
 */
export const JumpTile: React.FC<{ label: string; value: React.ReactNode; detail: React.ReactNode; action: string; onClick: () => void; tone?: 'bad' }> = ({
  label,
  value,
  detail,
  action,
  onClick,
  tone,
}) => (
  <Card variant="outlined" sx={{ height: '100%', ...(tone === 'bad' ? { borderColor: 'warning.main' } : {}) }}>
    <CardActionArea sx={{ height: '100%', alignItems: 'flex-start', display: 'flex' }} onClick={onClick}>
      <CardContent sx={{ p: 2, width: '100%' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
        <Typography variant="h4" component="div" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
          {detail}
        </Typography>
        <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 1, fontWeight: 600 }}>
          {action} <ChevronRight sx={{ fontSize: 16 }} />
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);
